import {
    CONTENT_REPORT_TYPE_OPTIONS,
    REPORT_TYPE_ALIASES,
    USER_REPORT_TYPE_OPTIONS,
    WORK_KIND_OPTIONS,
} from './constants';
import { DRAMA_STATUS_QUERY_ALL } from '@/pages/drama-review/utils';

import type { Dayjs } from 'dayjs';
import type {
    UgcReportBelongingWork,
    UgcReportListParams,
    UgcReportLog,
    UgcReportTargetType,
    UgcReportTicket,
    UgcReportTimePreset,
    UgcReportUser,
} from '@/services/ugc-reports';

export type ReportReasonDictionary = 'content' | 'user';

const trimOrUndefined = (value: unknown) => {
    const text = String(value ?? '').trim();
    return text || undefined;
};

const digitsOrUndefined = (value: unknown) => {
    const text = String(value ?? '').replace(/\D/g, '');
    return text || undefined;
};

export type UgcReportSearchFormValues = {
    report_time?: UgcReportTimePreset;
    reported_at_range?: [Dayjs, Dayjs];
    report_type_code?: string;
    work_kind?: UgcReportListParams['work_kind'];
    work_id?: string;
    work_name?: string;
    drama_id?: string;
    drama_name?: string;
    reported_user_id?: string;
    reported_user_nickname?: string;
    comment_id?: string;
    comment_user_id?: string;
    comment_user_nickname?: string;
    belonging_work_id?: string;
    belonging_work_name?: string;
    author_user_id?: string;
    author_name?: string;
    reporter_user_id?: string;
    reporter_nickname?: string;
};

export const buildUgcReportListParams = (
    targetType: UgcReportTargetType,
    values: UgcReportSearchFormValues,
    page: number,
    size: number,
): UgcReportListParams => {
    const params: UgcReportListParams = {
        target_type: targetType,
        page,
        size,
    };

    const reportTime = values.report_time;
    if (reportTime) {
        params.report_time = reportTime;
    }

    if (reportTime === 'CUSTOM' && values.reported_at_range?.length === 2) {
        params.reported_at = [
            values.reported_at_range[0].startOf('d').toISOString(),
            values.reported_at_range[1].endOf('d').toISOString(),
        ];
    }

    const reportTypeCode = trimOrUndefined(values.report_type_code);
    if (reportTypeCode) params.report_type_code = reportTypeCode;

    const workKind = trimOrUndefined(values.work_kind);
    if (workKind) params.work_kind = workKind as UgcReportListParams['work_kind'];

    const scalarFields: Array<keyof UgcReportSearchFormValues> = [
        'work_name',
        'drama_name',
        'reported_user_nickname',
        'comment_user_nickname',
        'belonging_work_name',
        'author_name',
        'reporter_nickname',
    ];
    scalarFields.forEach((key) => {
        const value = trimOrUndefined(values[key]);
        if (value) (params as Record<string, string>)[key] = value;
    });

    const idFields: Array<keyof UgcReportSearchFormValues> = [
        'work_id',
        'drama_id',
        'reported_user_id',
        'comment_id',
        'comment_user_id',
        'belonging_work_id',
        'author_user_id',
        'reporter_user_id',
    ];
    idFields.forEach((key) => {
        const value = digitsOrUndefined(values[key]);
        if (value) (params as Record<string, string>)[key] = value;
    });

    return params;
};

export const normalizeReportTypeCode = (code?: string | null) => {
    if (!code) return undefined;
    const raw = String(code).trim();
    const lower = raw.toLowerCase();
    return REPORT_TYPE_ALIASES[raw] ?? REPORT_TYPE_ALIASES[lower] ?? lower;
};

export const getWorkKindMeta = (kind?: string | null) =>
    WORK_KIND_OPTIONS.find((item) => item.value === kind);

export const getReportTypeMeta = (
    code: string | null | undefined,
    dictionary: ReportReasonDictionary,
) => {
    const raw = String(code ?? '').trim().toLowerCase();
    const normalized = normalizeReportTypeCode(code);
    if (!raw && !normalized) return undefined;

    const primary =
        dictionary === 'user' ? USER_REPORT_TYPE_OPTIONS : CONTENT_REPORT_TYPE_OPTIONS;
    const fallback =
        dictionary === 'user' ? CONTENT_REPORT_TYPE_OPTIONS : USER_REPORT_TYPE_OPTIONS;

    const match = (options: readonly { code: string; color: string }[]) =>
        options.find(
            (item) =>
                item.code === raw ||
                item.code === normalized ||
                normalizeReportTypeCode(item.code) === normalized,
        );

    return match(primary) ?? match(fallback);
};

export const formatReportTypeLabel = (
    code: string | null | undefined,
    dictionary: ReportReasonDictionary,
    formatMessage: (descriptor: { id: string; defaultMessage?: string }) => string,
    messages: Record<string, unknown>,
) => {
    const raw = String(code ?? '').trim().toLowerCase();
    const normalized = normalizeReportTypeCode(code);
    if (!raw && !normalized) {
        return formatMessage({ id: 'ugcReports.contentType.unknown' });
    }

    const prefixes =
        dictionary === 'user'
            ? ['ugcReports.userType.', 'ugcReports.contentType.']
            : ['ugcReports.contentType.', 'ugcReports.userType.'];

    for (const candidate of [raw, normalized]) {
        if (!candidate) continue;
        for (const prefix of prefixes) {
            const id = `${prefix}${candidate}`;
            if (messages[id]) {
                return formatMessage({ id });
            }
        }
    }

    return formatMessage({
        id: `${prefixes[0]}unknown`,
        defaultMessage: normalized || raw,
    });
};

export const aggregateReportReasons = (logs?: UgcReportLog[]) => {
    const counts = new Map<string, number>();
    (logs ?? []).forEach((log) => {
        const code = normalizeReportTypeCode(log.report_type_code);
        if (!code) return;
        counts.set(code, (counts.get(code) ?? 0) + 1);
    });
    return [...counts.entries()].map(([code, count]) => ({ code, count }));
};

export const getTargetString = (target: Record<string, unknown> | null | undefined, key: string) => {
    const value = target?.[key];
    if (value === undefined || value === null || value === '') return '';
    return String(value);
};

export const getUserNickname = (user?: UgcReportUser | null) =>
    getTargetString(user, 'nickname') || getTargetString(user, 'name');

export const getUserId = (user?: UgcReportUser | null) => getTargetString(user, 'id');

export const getAuthorNickname = (record: UgcReportTicket) =>
    getUserNickname(record.author) ||
    getTargetString(record.target, 'author_nickname') ||
    getTargetString(record.target, 'author_name') ||
    getTargetString(record.target, 'nickname');

export const getAuthorUserId = (record: UgcReportTicket) =>
    record.author_user_id || getUserId(record.author) || getTargetString(record.target, 'user_id');

export const getReporterNickname = (log: UgcReportLog) =>
    log.reporter_name?.trim() ||
    getUserNickname(log.reporter) ||
    getTargetString(log, 'reporter_nickname');

export const getReporterUserId = (log: UgcReportLog) =>
    log.reporter_user_id || getUserId(log.reporter);

export const getCommentUserNickname = (record: UgcReportTicket) =>
    getUserNickname(record.comment_user) ||
    getUserNickname(record.author) ||
    getTargetString(record.target, 'user_nickname') ||
    getTargetString(record.target, 'nickname');

export const getCommentUserId = (record: UgcReportTicket) =>
    getUserId(record.comment_user) ||
    record.author_user_id ||
    getUserId(record.author) ||
    getTargetString(record.target, 'user_id') ||
    getTargetString(record.target, 'comment_user_id');

export const getCommentContent = (record: UgcReportTicket) =>
    getTargetString(record.target, 'content') || getTargetString(record.target, 'text');

export const formatBelongingWorkTitle = (
    work: UgcReportBelongingWork | null,
    formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string,
) => {
    if (!work) return '';
    const workTitle = work.title ? String(work.title).trim() : '';
    if (!workTitle) return '';
    const episodeNo =
        work.episode_no !== undefined && work.episode_no !== null
            ? String(work.episode_no)
            : '';
    if (episodeNo) {
        return formatMessage(
            { id: 'ugcReports.columns.belongingWorkWithEpisode' },
            { title: workTitle, no: episodeNo },
        );
    }
    return workTitle;
};

export const getBelongingWork = (record: UgcReportTicket): UgcReportBelongingWork | null => {
    if (record.belonging_work) return record.belonging_work;
    const target = record.target;
    const workId =
        getTargetString(target, 'work_id') ||
        getTargetString(target, 'belonging_work_id') ||
        getTargetString(target, 'item_id') ||
        record.episode_id ||
        undefined;
    const workTitle =
        getTargetString(target, 'work_title') ||
        getTargetString(target, 'belonging_work_name') ||
        getTargetString(target, 'item_title');
    const episodeNo =
        getTargetString(target, 'episode_no') || getTargetString(target, 'episode_number');
    const dramaId = record.drama_id || getTargetString(target, 'drama_id') || undefined;
    if (!workId && !workTitle && !episodeNo && !dramaId) return null;
    return {
        id: workId,
        title: workTitle || undefined,
        episode_no: episodeNo || undefined,
        drama_id: dramaId,
    };
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
};

const firstText = (...values: unknown[]) => {
    for (const value of values) {
        const text = String(value ?? '').trim();
        if (text) return text;
    }
    return '';
};

export const getBelongingDramaPresentation = (record: UgcReportTicket) => {
    const work = getBelongingWork(record);
    const target = record.target;
    const episode = asRecord(target?.episode) || asRecord(work?.episode);
    const drama = asRecord(work?.drama) || asRecord(target?.drama) || asRecord(episode?.drama);

    return {
        dramaId: firstText(
            work?.drama_id,
            record.drama_id,
            getTargetString(target, 'drama_id'),
            getTargetString(drama, 'id'),
            getTargetString(episode, 'drama_id'),
        ),
        title: firstText(
            getTargetString(drama, 'title'),
            getTargetString(target, 'drama_title'),
            getTargetString(target, 'drama_name'),
            getTargetString(work, 'drama_title'),
            getTargetString(work, 'drama_name'),
            work?.title,
            getTargetString(episode, 'title'),
            getTargetString(target, 'title'),
        ),
        cover: firstText(
            getTargetString(drama, 'cover_url'),
            getTargetString(drama, 'cover'),
            getTargetString(target, 'drama_cover_url'),
            getTargetString(work, 'drama_cover_url'),
            work?.cover_url,
            getTargetString(episode, 'cover_url'),
            getTargetString(target, 'cover_url'),
        ),
    };
};

export const getWorkVideoUrl = (target?: Record<string, unknown> | null) => {
    const hls = getTargetString(target, 'hls_url');
    if (hls) return hls;
    return getTargetString(target, 'video_url');
};

export const getWorkCoverUrl = (target?: Record<string, unknown> | null) =>
    getTargetString(target, 'cover_url');

export const getDetailLink = (record: UgcReportTicket) => {
    if (record.target_type === 'WORK') {
        const isShortVideo = record.work_kind === 'SHORT_VIDEO';
        const dramaId =
            record.drama_id ||
            getTargetString(record.target, 'drama_id') ||
            // 短视频没有独立列表页，只能借所属短剧定位；拿不到剧集 ID 时不跳转
            (isShortVideo ? '' : record.target_id);
        if (!dramaId) return '';
        const params = new URLSearchParams({
            id: dramaId,
            drama_status: DRAMA_STATUS_QUERY_ALL,
        });
        if (record.work_kind === 'DRAMA_EPISODE' || isShortVideo) {
            const episodeId = record.episode_id || record.target_id;
            const episodeNo =
                getTargetString(record.target, 'episode_no') ||
                getTargetString(record.target, 'episode_number');
            if (episodeId) params.set('episode_id', episodeId);
            if (episodeNo) params.set('episode_no', episodeNo);
        }
        return `/dramas/drama-review?${params.toString()}`;
    }
    if (record.target_type === 'DRAMA') {
        const dramaId = record.drama_id || record.target_id;
        const params = new URLSearchParams({
            id: dramaId,
            drama_status: DRAMA_STATUS_QUERY_ALL,
        });
        return `/dramas/drama-review?${params.toString()}`;
    }
    if (record.target_type === 'USER') {
        return `/users/users?id=${encodeURIComponent(record.target_id)}`;
    }
    if (record.target_type === 'COMMENT') {
        const commentId = record.comment_id || record.target_id;
        return `/dramas/comments?id=${encodeURIComponent(commentId)}`;
    }
    return '';
};

export const formatEpisodeCount = (target?: Record<string, unknown> | null) => {
    const count =
        target?.episode_count ??
        target?.total_episodes ??
        target?.episodes_count ??
        target?.episode_total;
    if (count === undefined || count === null || count === '') return '';
    return String(count);
};

export const formatDisplayId = (id?: string | null) => {
    const text = String(id ?? '').trim();
    return text ? `#${text}` : '-';
};

export const formatBookTitle = (title?: string | null) => {
    const text = String(title ?? '').trim();
    if (!text) return '-';
    return text.startsWith('《') ? text : `《${text}》`;
};

export const createDefaultSearchFormValues = (withTimeFilter = true): UgcReportSearchFormValues => ({
    report_time: withTimeFilter ? 'LAST_7_DAYS' : undefined,
    reported_at_range: undefined,
    report_type_code: undefined,
    work_kind: undefined,
    work_id: '',
    work_name: '',
    drama_id: '',
    drama_name: '',
    reported_user_id: '',
    reported_user_nickname: '',
    comment_id: '',
    comment_user_id: '',
    comment_user_nickname: '',
    belonging_work_id: '',
    belonging_work_name: '',
    author_user_id: '',
    author_name: '',
    reporter_user_id: '',
    reporter_nickname: '',
});

export const defaultSearchFormValues = (): UgcReportSearchFormValues =>
    createDefaultSearchFormValues(true);

export const defaultSearchFormValuesWithoutTime = (): UgcReportSearchFormValues =>
    createDefaultSearchFormValues(false);
